import React, { useCallback, useEffect, useState } from 'react';
import {
  ATMSegment,
  ATMHeader,
  ATMGrid,
  ATMField,
  ATMInput,
  ATMSelect,
  ATMTextArea,
  ATMDivider,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import FormFlagUpload from 'src/components/organisms/form-flag-upload/form-flag-upload.component';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import { FileCategory } from 'src/constants';
import { getFlightTypeStatus } from 'src/selectors/flight-type.selector';
import { getFlightOperatorStatus } from 'src/selectors/flight-operator.selector';
import { getPilotStatus } from 'src/selectors/pilot.selector';
import { useFlightOperatorContext } from 'src/contexts/flight-operator.context';
import { usePilotContext } from 'src/contexts/pilot.context';
import { actionTypes } from 'src/ducks/flight-type.duck';
import { actionTypes as operatorActionTypes } from 'src/ducks/flight-operator.duck';
import { actionTypes as pilotActionTypes } from 'src/ducks/pilot.duck';
import { useFlightTypeContext } from 'src/contexts/flight-type.context';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import { useDroneAreasContext } from 'src/contexts/drone-areas.context';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import FlightRequestFlightArea from '../../flight-request-flight-area/flight-request-flight-area.component';
import style from '../flight-request-drone.module.scss';

type IProps = IRequestFormMethods & {
  isEdit?: boolean;
};

const FlightRequestDroneContractor: React.FC<IProps> = ({
  control,
  errors,
  isEdit: _isEdit,
}) => {
  const { state, actions } = useFlightTypeContext();
  const { state: operatorState, actions: operatorActions } =
    useFlightOperatorContext();
  const { state: pilotState, actions: pilotActions } = usePilotContext();

  useEffect(() => {
    actions.listGET();
    operatorActions.listGET();
    pilotActions.listGET();
  }, [pilotActions, actions]);

  const status = getFlightTypeStatus(state, actionTypes.FLIGHT_TYPE_FETCH);
  const operatorStatus = getFlightOperatorStatus(
    operatorState,
    operatorActionTypes.FLIGHT_OPERATOR_FETCH
  );
  const pilotStatus = getPilotStatus(pilotState, pilotActionTypes.PILOT_FETCH);

  const { state: sCommunitiesState, actions: sCommunitiesActions } = useSensitiveCommunitiesContext();
  const { state: droneAreasState, actions: droneAreasActions } = useDroneAreasContext();
  const { state: sCitiesState, actions: sCitiesActions } = useSensitiveCitiesContext();
  const [sCityPamLead, setSCityPamLead] = useState<string | null>('');
  const [sCommunityPamLead, setSCommunityPamLead] = useState<string | null>('');

  useEffect(() => {
    if (sCommunitiesActions && sCitiesActions && droneAreasActions) {
      sCommunitiesActions.listGET();
      sCitiesActions.listGET();
      droneAreasActions.listGET();
    }
  }, [sCommunitiesActions, sCitiesActions, droneAreasActions]);

  const handleChange = useCallback(
    (value, type) => {
      if (type === 'city') {
        if (!value) {
          setSCityPamLead(null);
        } else {
          const sCity = sCitiesState.list.find((v) => v.sensitiveCity === value);
          setSCityPamLead(sCity.pamLead);
        }
        control.setValue('droneFlightContractor.sensitiveCity', value);
      } else if (type === 'drone') {
        control.setValue('droneFlightContractor.droneArea', value);
      } else {
        if (!value) {
          setSCommunityPamLead(null);
        } else {
          const sCommunity = sCommunitiesState.list.find((v) => v.sensitiveCommunity === value);
          setSCommunityPamLead(sCommunity.sensitiveCommunityPamLead);
        }
        control.setValue('droneFlightContractor.sensitiveCommunity', value);
      }
    },
    [state, control, sCitiesState, sCommunitiesState]
  );

const [sensitiveCustomerFlag, setSensitiveCustomerFlag] = useState(control?.defaultValuesRef?.current?.droneFlightContractor?.sensitiveFlag || false);
const defaultSCityPamLead = sCitiesState.list.find((v) => v.sensitiveCity === control?.defaultValuesRef?.current?.droneFlightContractor?.sensitiveCity)?.pamLead;
const defaultSCommunityPamLead = sCommunitiesState.list.find((v) => v.sensitiveCommunity === control?.defaultValuesRef?.current?.droneFlightContractor?.sensitiveCommunity)?.sensitiveCommunityPamLead;
  return (
    <ATMSegment color="teal" className={style.wrapper}>
      <ATMHeader>{Lang.TTL_DRONE_FLIGHT_CONTRACTOR}</ATMHeader>

      <ATMGrid>
        <ATMGrid.Row columns={2}>
          <ATMGrid.Column width="5">
            <ATMField
              name="droneFlightContractor.flightType"
              label={<LabelRequired>{Lang.LBL_FLIGHT_TYPE}</LabelRequired>}
              as={ATMSelect}
              selectOnBlur={false}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.list.map((value) => ({
                key: value.flightTypeId,
                value: value.flightType,
                text: value.flightType,
              }))}
              search
              clearable
              error={errors.droneFlightContractor?.flightType}
              onChange={([_, { value }]) => value}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.purpose"
              label={<LabelRequired>{Lang.LBL_PURPOSE}</LabelRequired>}
              maxLength={200}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.purpose}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row columns={4}>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.flightOperator"
              label={<LabelRequired>{Lang.LBL_FLIGHT_OPERATOR}</LabelRequired>}
              as={ATMSelect}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={operatorState.list.map((value) => ({
                key: value.operatorId,
                value: value.name,
                text: value.name,
              }))}
              search
              error={errors.droneFlightContractor?.flightOperator}
              onChange={([_, { value }]) => value}
              loading={!operatorState.list.length && operatorStatus.fetching}
              disabled={!operatorState.list.length && operatorStatus.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.pilotName"
              label={<LabelRequired>{Lang.LBL_PILOT_IN_COMMAND}</LabelRequired>}
              as={ATMSelect}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={pilotState.list.map((value) => ({
                key: value.pilotId,
                value: value.name,
                text: value.name,
              }))}
              search
              error={errors.droneFlightContractor?.pilotName}
              onChange={([_, { value }]) => value}
              loading={!pilotState.list.length && pilotStatus.fetching}
              disabled={!pilotState.list.length && pilotStatus.fetching}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.address"
              label={
                <LabelRequired>{Lang.LBL_ADDRESS_CROSS_STREET}</LabelRequired>
              }
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.address}
              style={{ width: '75%' }}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.project"
              label={<LabelRequired>{Lang.LBL_PROJECT}</LabelRequired>}
              as={ATMInput}
              maxLength={50}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.project}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.destination"
              label={<LabelRequired>{Lang.LBL_TOWN_CITY}</LabelRequired>}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.destination}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.asset"
              label={Lang.LBL_FACILITY_ASSET_NUMBER}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.asset}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.circuit"
              label={<LabelRequired>{Lang.LBL_PIPELINE_NUMBER}</LabelRequired>}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightContractor?.circuit}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_CONTACT_INFORMATION}
      </ATMHeader>

      <ATMGrid>
        <ATMGrid.Row columns={4}>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.primeName"
              label={<LabelRequired>{Lang.LBL_PRIME_CONTRACTOR}</LabelRequired>}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={errors.droneFlightContractor?.primeName}
              onChange={([_, { value }]) => value}
              autoComplete="off"
            />
          </ATMGrid.Column>

          <ATMGrid.Column>
            <ATMField
              name="droneFlightContractor.primeEmail"
              label={<LabelRequired>{Lang.LBL_CONTRACTOR_EMAIL}</LabelRequired>}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={errors.droneFlightContractor?.primeEmail}
              onChange={([_, { value }]) => value}
              autoComplete="off"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="droneFlightContractor.nightFlag"
            label={Lang.LBL_NIGHT_FLIGHT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        {control?.defaultValuesRef?.current?.droneFlightContractor
          ?.isOldFlight ? (
          <>
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.sensitiveFlag"
                listName="droneFlightContractor.sensitiveFiles"
                label={Lang.LBL_SENSITIVE_CUSTOMER}
                allowedTypes={[FileCategory.SensitiveCustomer]}
                control={control}
                errors={errors}
                setValue={setSensitiveCustomerFlag}
              />
            </ATMGrid.Column>
            {sensitiveCustomerFlag && (
  <ATMGrid columns={4}>
  <ATMGrid.Column>
        <ATMField
              name="droneFlightContractor.sensitiveCity"
              label={Lang.LBL_SENSITIVE_CITY}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCitiesState.list.map((item) => ({
                key: item.sensitiveCityId,
                value: item.sensitiveCity,
                text: item.sensitiveCity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'city');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.sensitiveCity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
        {!(sCityPamLead === null) && (sCityPamLead || defaultSCityPamLead) && <>
        <div>City PAM Lead:</div>
        <div>{sCityPamLead || defaultSCityPamLead}</div>
        </>}
 </ATMGrid.Column>

        <ATMGrid.Column>
<ATMField
              name="droneFlightContractor.sensitiveCommunity"
              label={Lang.LBL_SENSITIVE_COMMUNITIES}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCommunitiesState.list.map((item) => ({
                key: item.sensitiveCommunityId,
                value: item.sensitiveCommunity,
                text: item.sensitiveCommunity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'community');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.sensitiveCommunity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
/>
{!(sCommunityPamLead === null) && (sCommunityPamLead || defaultSCommunityPamLead) && <>
        <div style={{ opacity: '80%' }}>Community PAM Lead:</div>
        <div>{sCommunityPamLead || defaultSCommunityPamLead}</div>
        </>}
 </ATMGrid.Column>
 <ATMGrid.Column>
        <ATMField
              name="droneFlightContractor.droneArea"
              label={Lang.LBL_DRONE_AREA}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={droneAreasState.list.map((item) => ({
                key: item.droneAreasId,
                value: item.SensitiveCityLocation,
                text: item.SensitiveCityLocation,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'drone');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.droneArea}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
 </ATMGrid.Column>
  </ATMGrid>)}
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.culturalFlag"
                listName="droneFlightContractor.culturalFiles"
                label={Lang.LBL_CULTURAL_RESTRICTION}
                allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
                control={control}
                errors={errors}
                note={Lang.NOTE_IF_YES_ENVIRONMENTAL_APPROVAL}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.landFlag"
                listName="droneFlightContractor.landFiles"
                label={Lang.LBL_LAND_MANAGEMENT_APPROVAL_REQUIREMENT}
                allowedTypes={[FileCategory.LandManagementApproval]}
                control={control}
                errors={errors}
                note={Lang.NOTE_IF_YES_LAND_APPROVAL}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.safetyFlag"
                listName="droneFlightContractor.safetyFiles"
                label={Lang.LBL_PUBLIC_SAFETY_CONCERNS}
                allowedTypes={[FileCategory.PublicSafetyConcerns]}
                control={control}
                errors={errors}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FormFlag
                name="droneFlightContractor.dnadeFlag"
                label={Lang.LBL_NOTIFICATION_SENT}
                control={control}
                errors={errors}
              />
            </ATMGrid.Column>{' '}
          </>
        ) : (
          <>
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.airspaceFlag"
                listName="droneFlightContractor.airspaceFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_1}
                allowedTypes={[FileCategory.AirspaceAuthorization]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_1_NOTE}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.safetyFlag"
                listName="droneFlightContractor.safetyFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_2}
                allowedTypes={[FileCategory.SafetyMitigations]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_2_NOTE}
                no={false}
                na
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlag
                name="droneFlightContractor.sensitiveFlag"
                label={Lang.LBL_DRONE_OTHER_QUESTION_3}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_3_NOTE}
                no={false}
                na
                setValue={setSensitiveCustomerFlag}
              />
            </ATMGrid.Column>
            {sensitiveCustomerFlag && (
  <ATMGrid columns={4}>
  <ATMGrid.Column>
        <ATMField
              name="droneFlightContractor.sensitiveCity"
              label={Lang.LBL_SENSITIVE_CITY}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCitiesState.list.map((item) => ({
                key: item.sensitiveCityId,
                value: item.sensitiveCity,
                text: item.sensitiveCity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'city');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.sensitiveCity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
        {!(sCityPamLead === null) && (sCityPamLead || defaultSCityPamLead) && <>
        <div>City PAM Lead:</div>
        <div>{sCityPamLead || defaultSCityPamLead}</div>
        </>}
 </ATMGrid.Column>

        <ATMGrid.Column>
<ATMField
              name="droneFlightContractor.sensitiveCommunity"
              label={Lang.LBL_SENSITIVE_COMMUNITIES}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCommunitiesState.list.map((item) => ({
                key: item.sensitiveCommunityId,
                value: item.sensitiveCommunity,
                text: item.sensitiveCommunity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'community');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.sensitiveCommunity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
/>
{!(sCommunityPamLead === null) && (sCommunityPamLead || defaultSCommunityPamLead) && <>
        <div style={{ opacity: '80%' }}>Community PAM Lead:</div>
        <div>{sCommunityPamLead || defaultSCommunityPamLead}</div>
        </>}
 </ATMGrid.Column>
 <ATMGrid.Column>
        <ATMField
              name="droneFlightContractor.droneArea"
              label={Lang.LBL_DRONE_AREA}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={droneAreasState.list.map((item) => ({
                key: item.droneAreasId,
                value: item.SensitiveCityLocation,
                text: item.SensitiveCityLocation,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'drone');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.droneFlightContractor?.droneArea}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
 </ATMGrid.Column>
  </ATMGrid>)}

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.landFlag"
                listName="droneFlightContractor.landFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_4}
                allowedTypes={[FileCategory.LandManagementApproval]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_4_NOTE}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightContractor.culturalFlag"
                listName="droneFlightContractor.culturalFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_5}
                allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_5_NOTE}
              />
            </ATMGrid.Column>
          </>
        )}
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_BRIEF_JOB_DESCRIPTION}
      </ATMHeader>
      <p>{Lang.MSG_BRIEF_DESCRIPTION}</p>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <ATMField
            name="droneFlightContractor.jobDesc"
            label={Lang.LBL_DESCRIPTION_COMMENT}
            as={ATMTextArea}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.droneFlightContractor?.jobDesc}
            style={{
              width: '75%',
              height: '250px',
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestFlightArea
        category="droneFlightContractor"
        control={control}
        errors={errors}
      />
      <ATMDivider />

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_SPECIAL_COMMENTS}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <ATMField
            name="droneFlightContractor.comments"
            label={Lang.LBL_COMMENT}
            as={ATMTextArea}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.droneFlightContractor?.comments}
            style={{
              width: '75%',
              height: '250px',
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </ATMSegment>
  );
};

export default FlightRequestDroneContractor;
